/**
 * 统一错误处理工具
 */

export interface ApiError {
  message: string;
  status?: number;
  response?: any;
}

/**
 * 判断是否为「用户主动取消请求」产生的错误
 *
 * 覆盖两类来源：
 * - fetch + AbortController：抛 DOMException，name === 'AbortError'
 * - axios + AbortController：抛 CanceledError，code === 'ERR_CANCELED'
 *
 * 取消是用户的预期操作，调用方应据此静默处理（不弹错误提示）
 */
export function isAbortError(error: any): boolean {
  if (!error) return false;
  return (
    error.name === 'AbortError' ||
    error.code === 'ERR_CANCELED' ||
    error.name === 'CanceledError' ||
    error.message === 'canceled' ||
    error.message === 'The user aborted a request.'
  );
}

/**
 * 从错误对象中提取用户友好的错误消息
 */
export function getErrorMessage(error: any): string {
  const data = error?.response?.data;

  // 1. FastAPI HTTPException: detail 是字符串
  if (typeof data?.detail === 'string' && data.detail) {
    return data.detail;
  }

  // 2. ApiErrorResponse 格式: { code, message, error: {...} }
  if (data?.message) {
    return data.message;
  }

  // 3. FastAPI HTTPException: detail 是对象（如 ApiErrorResponse dict）
  if (data?.detail?.message) {
    return data.detail.message;
  }

  // 4. Pydantic 校验错误: detail 是数组
  if (Array.isArray(data?.detail) && data.detail.length > 0) {
    const first = data.detail[0];
    if (first?.msg) {
      const loc = Array.isArray(first.loc) ? first.loc.join('.') : '';
      return loc ? `${loc}: ${first.msg}` : first.msg;
    }
  }

  // 5. 尝试提取错误对象中的 message
  if (error?.message) {
    return error.message;
  }

  // 6. 尝试提取错误对象中的 detail
  if (error?.detail) {
    return error.detail;
  }

  // 7. 默认错误消息
  return '操作失败，请重试';
}

/**
 * 从错误对象中提取完整的错误信息（包含状态码等）
 */
export function getApiError(error: any): ApiError {
  return {
    message: getErrorMessage(error),
    status: error?.response?.status,
    response: error?.response?.data,
  };
}

/**
 * 显示错误消息（配合 Ant Design message 使用）
 */
export function showError(message: any, defaultMessage: string = '操作失败，请重试'): string {
  const errorMessage = getErrorMessage(message);
  return errorMessage || defaultMessage;
}
